import React, { useContext, useEffect, useState, useMemo } from "react";
import GroupReportContext from "../../../../Context/GroupReportContext";
import { applyFullTieOrder } from "../../../../utils/quadrantTieOrder";

// Hierarchy for tie-breaking: lower number = higher priority
const QUADRANT_HIERARCHY = {
  heart: 0,      // Highest priority
  soul: 1,
  mind: 2,
  strength: 3    // Lowest priority
};

// Comparator function for sorting quadrants with tie-breaking
const compareQuadrants = (a, b) => {
  // First compare by value (descending)
  const valueDiff = b.value - a.value;
  if (valueDiff !== 0) return valueDiff;
  
  // If values are equal, compare by hierarchy (ascending - lower number = higher priority)
  const aHierarchy = QUADRANT_HIERARCHY[a.name.toLowerCase()] ?? 999;
  const bHierarchy = QUADRANT_HIERARCHY[b.name.toLowerCase()] ?? 999;
  return aHierarchy - bHierarchy;
};

// Comparator for Object.entries format [name, value]
const compareQuadrantEntries = (a, b) => {
  // First compare by value (descending)
  const valueDiff = b[1] - a[1];
  if (valueDiff !== 0) return valueDiff;
  
  // If values are equal, compare by hierarchy
  const aHierarchy = QUADRANT_HIERARCHY[a[0].toLowerCase()] ?? 999;
  const bHierarchy = QUADRANT_HIERARCHY[b[0].toLowerCase()] ?? 999;
  return aHierarchy - bHierarchy;
};

export default function GroupQuardantsFlow({ size = 240, radius="8.5", showFlowText= true }) {
  const [arrowPath, setArrowPath] = useState("");
  const [dotPosition, setDotPosition] = useState({ x: 0, y: 0 });
  const { averagePercentages } = useContext(GroupReportContext);

  const diagramSize = size;
  const headLength = 17;
  const headWidth = 11;
  const centerOffset = diagramSize / 2;
  const quadrantOffset = centerOffset / 2;

  const quadrantCenters = useMemo(() => ({
    mind: { x: quadrantOffset, y: quadrantOffset },
    strength: { x: diagramSize - quadrantOffset, y: quadrantOffset },
    soul: { x: quadrantOffset, y: diagramSize - quadrantOffset },
    heart: { x: diagramSize - quadrantOffset, y: diagramSize - quadrantOffset },
  }), [diagramSize, quadrantOffset]);

  const getCtxAvgRaw = (labelLower) => {
    const rec = averagePercentages?.find(el => el?.name?.toLowerCase() === labelLower);
    return Number(rec?.avg ?? 0) || 0; 
  };

  const fmtPct = (labelLower) => `${getCtxAvgRaw(labelLower).toFixed(2)}%`;

  // ---------------- flow text calculation ----------------
  const flowText = useMemo(() => {
    if (!averagePercentages || !averagePercentages.length) return "";

    const sorted = applyFullTieOrder(
      [...averagePercentages]
        .map(el => ({ name: el.name.toLowerCase(), value: Number(el.avg) || 0 }))
        .sort(compareQuadrants),
      (q) => q.name,
      (q) => q.value
    );

    const first = sorted[0]?.name?.toUpperCase();
    const second = sorted[1]?.name?.toUpperCase();

    return (first && second) ? `YOU FLOW FROM ${first} TO ${second}` : "";
  }, [averagePercentages]);
  // --------------------------------------------------------

  const [arrowHeadPoints, setArrowHeadPoints] = useState("");

  useEffect(() => {
    const avg = {
      mind: getCtxAvgRaw("mind"),
      strength: getCtxAvgRaw("strength"),
      soul: getCtxAvgRaw("soul"),
      heart: getCtxAvgRaw("heart"),
    };

    const sorted = applyFullTieOrder(
      Object.entries(avg).sort(compareQuadrantEntries),
      ([name]) => name,
      ([, value]) => value
    ).map(([name]) => name);

    if (!sorted.length) {
      setArrowPath("");
      setDotPosition({ x: 0, y: 0 });
      setArrowHeadPoints("");
      return;
    }

    let path = "";
    const points = sorted.map((q) => quadrantCenters[q]).filter(Boolean);
    const startPt = points[0] || null;
    if (points.length) {
      const pointsForPath = [...points];
      if (pointsForPath.length > 1) {
        const tip = pointsForPath[pointsForPath.length - 1];
        const prev = pointsForPath[pointsForPath.length - 2];
        const dx = tip.x - prev.x;
        const dy = tip.y - prev.y;
        const len = Math.hypot(dx, dy) || 1;
        const trimmedTip = {
          x: tip.x - (headLength * dx) / len,
          y: tip.y - (headLength * dy) / len,
        };
        pointsForPath[pointsForPath.length - 1] = trimmedTip;
      }

      path = `M ${pointsForPath[0].x} ${pointsForPath[0].y}`;
      for (let i = 1; i < pointsForPath.length; i++) {
        path += ` L ${pointsForPath[i].x} ${pointsForPath[i].y}`;
      }
    }

    let headPoints = "";
    if (points.length > 1) {
      const tip = points[points.length - 1];
      const prev = points[points.length - 2];
      const angle = Math.atan2(tip.y - prev.y, tip.x - prev.x);
      const left = {
        x: tip.x - headLength * Math.cos(angle) + headWidth * Math.sin(angle),
        y: tip.y - headLength * Math.sin(angle) - headWidth * Math.cos(angle),
      };
      const right = {
        x: tip.x - headLength * Math.cos(angle) - headWidth * Math.sin(angle),
        y: tip.y - headLength * Math.sin(angle) + headWidth * Math.cos(angle),
      };
      headPoints = `${tip.x},${tip.y} ${left.x},${left.y} ${right.x},${right.y}`;
    }

    setArrowPath(path);
    setDotPosition(startPt || { x: 0, y: 0 });
    setArrowHeadPoints(headPoints);
  }, [averagePercentages, headLength, headWidth, quadrantCenters]);

  return (
    <div>
      <div
        style={{
          position: "relative",
          width: diagramSize,
          height: diagramSize,
          backgroundColor: "transparent",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          border: "2px solid #000",
          margin: "16px auto",
        }}
      >
        {/* grid lines */}
        <div style={{ position:"absolute", width:2, height:"100%", background:"#000", left:"50%", top:0, transform:"translateX(-50%)" }} />
        <div style={{ position:"absolute", width:"100%", height:2, background:"#000", top:"50%", left:0, transform:"translateY(-50%)" }} />

        {/* quadrant labels */}
        <div style={{ position:"absolute", top:6, left:10, color:"#000", fontSize:10, fontWeight:700, fontFamily:"Helvetica, sans-serif" }}>MIND</div>
        <div style={{ position:"absolute", top:6, right:10, color:"#000", fontSize:10, fontWeight:700, fontFamily:"Helvetica, sans-serif" }}>STRENGTH</div>
        <div style={{ position:"absolute", bottom:6, left:10, color:"#000", fontSize:10, fontWeight:700, fontFamily:"Helvetica, sans-serif" }}>SOUL</div>
        <div style={{ position:"absolute", bottom:6, right:10, color:"#000", fontSize:10, fontWeight:700, fontFamily:"Helvetica, sans-serif" }}>HEART</div>

        {/* percentages */}
        <div style={{ position:"absolute", top:20, left:10,  color:"#000", fontSize:10, fontWeight:700, fontFamily:"Helvetica, sans-serif" }}>{fmtPct("mind")}</div>
        <div style={{ position:"absolute", top:20, right:10, color:"#000", fontSize:10, fontWeight:700, fontFamily:"Helvetica, sans-serif" }}>{fmtPct("strength")}</div>
        <div style={{ position:"absolute", bottom:20, left:10,  color:"#000", fontSize:10, fontWeight:700, fontFamily:"Helvetica, sans-serif" }}>{fmtPct("soul")}</div>
        <div style={{ position:"absolute", bottom:20, right:10, color:"#000", fontSize:10, fontWeight:700, fontFamily:"Helvetica, sans-serif" }}>{fmtPct("heart")}</div>

        {/* arrow svg */}
        <svg width={diagramSize} height={diagramSize} style={{ position: "absolute", top: 0, left: 0 }}>
          <circle cx={dotPosition.x} cy={dotPosition.y} r={radius} fill="#B6111F" />
          {arrowPath && (
            <path d={arrowPath} stroke="#B6111F" strokeWidth="4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          )}
          {arrowHeadPoints && (
            <polygon points={arrowHeadPoints} fill="#B6111F" stroke="#B6111F" />
          )}
        </svg>
      </div>

     {
      showFlowText &&  <div style={{  fontWeight: 700, fontSize: '11px', lineHeight: '15px', color: '#00577D', textAlign: 'center', marginTop: '16px',}}>
        {flowText}
      </div>
     }
    </div>
  );
}
